import { useId, CSSProperties, useState } from 'react'

interface SpectrophotometerProps {
  wavelength?: number // nm (typically 200-800)
  absorbance?: number // A (typically 0-3)
  sampleIn?: boolean
  onInsertSample?: () => void
  onSetWavelength?: (wavelength: number) => void
  disabled?: boolean
  className?: string
}

const Spectrophotometer = ({
  wavelength = 540,
  absorbance = 0.000,
  sampleIn = false,
  onInsertSample,
  onSetWavelength,
  disabled = false,
  className = ''
}: SpectrophotometerProps) => {
  const id = useId()
  const [isHovered, setIsHovered] = useState(false)

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  const sampleHolderStyle: CSSProperties = {
    cursor: disabled ? 'default' : onInsertSample ? 'pointer' : 'default'
  }

  return (
    <svg
      viewBox="0 0 220 140"
      width={280}
      height={200}
      style={containerStyle}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <defs>
        {/* Instrument body gradient */}
        <linearGradient id={`body-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3A3A3A" />
          <stop offset="50%" stopColor="#2C2C2C" />
          <stop offset="100%" stopColor="#1A1A1A" />
        </linearGradient>

        {/* Side panel gradient */}
        <linearGradient id={`side-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2A2A2A" />
          <stop offset="100%" stopColor="#1A1A1A" />
        </linearGradient>

        {/* Display glow */}
        <filter id={`display-glow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-5%" y="-5%" width="115%" height="120%">
          <feDropShadow dx="3" dy="4" stdDeviation="3" floodOpacity="0.4" />
        </filter>

        {/* Cuvette glass */}
        <linearGradient id={`cuvette-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
        </linearGradient>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Main body */}
        <rect
          x="10"
          y="25"
          width="200"
          height="90"
          fill={`url(#body-${id})`}
          rx="8"
        />

        {/* Top panel - slightly lighter */}
        <rect
          x="10"
          y="25"
          width="200"
          height="15"
          fill="#3D3D3D"
          rx="8"
        />
        <rect
          x="10"
          y="32"
          width="200"
          height="8"
          fill="#3D3D3D"
        />

        {/* Side accent */}
        <rect
          x="10"
          y="25"
          width="8"
          height="90"
          fill={`url(#side-${id})`}
          rx="8"
        />

        {/* Brand label area */}
        <rect
          x="20"
          y="30"
          width="50"
          height="8"
          fill="#FF5A36"
          rx="2"
        />
        <text
          x="45"
          y="37"
          fontSize="6"
          fill="white"
          textAnchor="middle"
          fontWeight="bold"
          fontFamily="Arial, sans-serif"
        >
          UV-VIS
        </text>

        {/* Main display panel */}
        <rect
          x="25"
          y="48"
          width="85"
          height="55"
          fill="#1A1A1A"
          stroke="#4A4A4A"
          strokeWidth="2"
          rx="4"
        />

        {/* Display screen */}
        <rect
          x="30"
          y="53"
          width="75"
          height="25"
          fill="#001a00"
          rx="2"
        />

        {/* Display content */}
        <g filter={`url(#display-glow-${id})`}>
          {/* Absorbance value */}
          <text
            x="35"
            y="68"
            fontSize="14"
            fill="#00A86B"
            fontFamily="'Courier New', monospace"
            fontWeight="bold"
          >
            A: {absorbance.toFixed(3)}
          </text>

          {/* Wavelength value */}
          <text
            x="35"
            y="95"
            fontSize="10"
            fill="#00A86B"
            fontFamily="'Courier New', monospace"
          >
            {wavelength} nm
          </text>
        </g>

        {/* Control buttons */}
        <g>
          {/* Zero button */}
          <rect x="32" y="80" width="22" height="10" fill="#4A4A4A" rx="2" />
          <text x="43" y="88" fontSize="6" fill="#E0E0E0" textAnchor="middle">ZERO</text>

          {/* Mode button */}
          <rect x="58" y="80" width="22" height="10" fill="#4A4A4A" rx="2" />
          <text x="69" y="88" fontSize="6" fill="#E0E0E0" textAnchor="middle">MODE</text>

          {/* Enter button */}
          <rect x="84" y="80" width="18" height="10" fill="#00A86B" rx="2" />
          <text x="93" y="88" fontSize="6" fill="white" textAnchor="middle">OK</text>
        </g>

        {/* Wavelength dial area */}
        <rect
          x="120"
          y="48"
          width="40"
          height="55"
          fill="#2A2A2A"
          rx="4"
        />
        <text
          x="140"
          y="58"
          fontSize="6"
          fill="#888"
          textAnchor="middle"
        >
          WAVELENGTH
        </text>

        {/* Dial */}
        <circle
          cx="140"
          cy="78"
          r="18"
          fill="#3A3A3A"
          stroke="#5A5A5A"
          strokeWidth="2"
        />
        <circle
          cx="140"
          cy="78"
          r="14"
          fill="#2A2A2A"
        />
        {/* Dial indicator */}
        <line
          x1="140"
          y1="78"
          x2="140"
          y2="66"
          stroke="#FF5A36"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Dial markings */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
          <line
            key={angle}
            x1={140 + 16 * Math.cos((angle * Math.PI) / 180)}
            y1={78 + 16 * Math.sin((angle * Math.PI) / 180)}
            x2={140 + 18 * Math.cos((angle * Math.PI) / 180)}
            y2={78 + 18 * Math.sin((angle * Math.PI) / 180)}
            stroke="#6A6A6A"
            strokeWidth="1"
          />
        ))}

        {/* Sample compartment */}
        <rect
          x="170"
          y="48"
          width="35"
          height="55"
          fill="#1A1A1A"
          stroke="#4A4A4A"
          strokeWidth="1"
          rx="4"
        />
        <text
          x="187"
          y="58"
          fontSize="5"
          fill="#888"
          textAnchor="middle"
        >
          SAMPLE
        </text>

        {/* Sample holder / cuvette slot */}
        <g
          style={sampleHolderStyle}
          onClick={!disabled && onInsertSample ? onInsertSample : undefined}
        >
          <rect
            x="178"
            y="62"
            width="18"
            height="35"
            fill="#0A0A0A"
            stroke={isHovered && onInsertSample ? '#FF5A36' : '#3A3A3A'}
            strokeWidth="1"
            rx="2"
          />

          {/* Cuvette if sample is in */}
          {sampleIn && (
            <g>
              <rect
                x="181"
                y="65"
                width="12"
                height="28"
                fill={`url(#cuvette-${id})`}
                stroke="#7A9BB8"
                strokeWidth="0.5"
                rx="1"
              />
              {/* Sample liquid */}
              <rect
                x="183"
                y="70"
                width="8"
                height="20"
                fill="#7DD3FC"
                opacity="0.7"
                rx="1"
              />
              {/* Cuvette shine */}
              <line
                x1="183"
                y1="68"
                x2="183"
                y2="88"
                stroke="rgba(255,255,255,0.4)"
                strokeWidth="1"
              />
            </g>
          )}

          {/* Insert indicator */}
          {!sampleIn && (
            <text
              x="187"
              y="82"
              fontSize="14"
              fill="#3A3A3A"
              textAnchor="middle"
            >
              +
            </text>
          )}
        </g>

        {/* Status LED */}
        <circle
          cx="200"
          cy="35"
          r="4"
          fill={sampleIn ? '#00A86B' : '#FF5A36'}
        >
          <animate
            attributeName="opacity"
            values="1;0.5;1"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Ventilation slots */}
        {[0, 1, 2, 3, 4].map((i) => (
          <rect
            key={i}
            x={20 + i * 8}
            y="108"
            width="5"
            height="2"
            fill="#1A1A1A"
            rx="0.5"
          />
        ))}
      </g>

      {/* Label */}
      <text
        x="110"
        y="135"
        fontSize="10"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Spectrophotometer
      </text>
    </svg>
  )
}

export default Spectrophotometer
